import React from 'react';
import { observer } from 'mobx-react-lite';
import { CSSProperties } from 'react';
import RSelect, { DropdownIndicatorProps, OptionProps, Props, components } from 'react-select';
import { themeState } from 'styleguide/theme';
import { Theme } from 'styleguide/types';
import { colors } from 'styleguide/colors';
import { CornerRadius } from 'styleguide/spacing';
import { View } from 'styleguide/View';
import { MicroIcon } from 'styleguide/Icons';
import { P2 } from 'styleguide/Texts';
import { FlexRow } from 'styleguide/Row';
import { textStyles } from 'styleguide/Texts';
import { t } from 'translations/t';

export const isUpdate = (x: unknown): x is { label: string; value: string } => {
  return !!(x as any)?.label && !!(x as any)?.value;
};

const controlStyle = (theme: Theme): CSSProperties => {
  return {
    minWidth: 100,
    borderRadius: CornerRadius.md,
    borderColor: theme.name === 'light' ? colors.greyLight : colors.greyDark,
    backgroundColor: theme.name === 'light' ? colors.white : colors.greyHiperDark,
    ...textStyles.p2,
  };
};

export const Multiselect = observer((props: Props<{ value: string; label: string }>) => {
  const theme = themeState.current;

  return (
    <RSelect
      isMulti={true}
      isSearchable={false}
      theme={theme => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary50: themeState.current.name === 'light' ? colors.greyUltraLight : colors.greyDark,
        },
      })}
      styles={{
        control: styles => ({ ...styles, ...controlStyle(theme) }),
        singleValue: styles => ({ ...styles, ...{ color: theme.primary } }),
        indicatorSeparator: styles => ({ ...styles, ...{ backgroundColor: 'transparent' } }),
        dropdownIndicator: styles => ({ ...styles, ...{ color: theme.primary } }),
        option: (styles, state) => ({
          ...styles,
          color: theme.primary,
          ...textStyles.p2,
          backgroundColor: state.isFocused
            ? theme.name === 'light'
              ? colors.greyUltraLight
              : colors.greyDark
            : theme.secondary,
        }),
        menu: styles => ({
          ...styles,
          ...{ backgroundColor: theme.name === 'light' ? colors.white : colors.greyUltraDark },
        }),
      }}
      components={{ DropdownIndicator: DropdownIndicator, Option: Option }}
      placeholder={t('global.select.ellipsis')}
      {...props}
    />
  );
});

interface SelectProps extends Props<{ value: string; label: string }> {
  controlStyle?: CSSProperties;
}

export const Select = observer((props: SelectProps) => {
  const theme = themeState.current;
  const { controlStyle: customControlStyle, ...rest } = props;

  return (
    <RSelect
      isMulti={false}
      isSearchable={false}
      theme={theme => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary50: themeState.current.name === 'light' ? colors.greyUltraLight : colors.greyDark,
        },
      })}
      styles={{
        control: styles => ({ ...styles, ...controlStyle(theme), ...customControlStyle }),
        singleValue: styles => ({ ...styles, ...{ color: theme.primary } }),
        placeholder: styles => ({
          ...styles,
          ...{
            color: colors.grey,
            ...textStyles.p2,
          },
        }),
        indicatorSeparator: styles => ({ ...styles, ...{ backgroundColor: 'transparent' } }),
        dropdownIndicator: styles => ({ ...styles, ...{ color: theme.primary } }),
        option: (styles, state) => ({
          ...styles,
          color: theme.primary,
          ...textStyles.p2,
          backgroundColor: state.isFocused
            ? theme.name === 'light'
              ? colors.greyUltraLight
              : colors.greyDark
            : theme.secondary,
        }),
        menu: styles => ({
          ...styles,
          ...{
            backgroundColor: theme.secondary,
            borderRadius: CornerRadius.lg,
            border: `1px solid ${theme.border}`,
            boxShadow: '0 2px 12px 0 rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
          },
        }),
      }}
      components={{ DropdownIndicator: DropdownIndicator, Option: Option }}
      placeholder={t('global.select.ellipsis')}
      {...rest}
    />
  );
});

const DropdownIndicator = (props: DropdownIndicatorProps<{ value: string; label: string }>) => {
  const theme = themeState.current;
  return (
    <components.DropdownIndicator {...props}>
      <View>
        <MicroIcon
          type="chevron-down"
          tint={theme.primary}
          style={{
            transform: props.selectProps.menuIsOpen ? 'rotate(-180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease-in-out',
          }}
        />
      </View>
    </components.DropdownIndicator>
  );
};

const Option = (props: OptionProps<{ value: string; label: string }>) => {
  return (
    <components.Option {...props}>
      <FlexRow style={{ justifyContent: 'space-between' }}>
        <P2>{props.label}</P2>
        <MicroIcon
          type="check"
          tint={colors.primary}
          style={{ display: props.isSelected ? 'block' : 'none' }}
        />
      </FlexRow>
    </components.Option>
  );
};
